/** Moving between cards in a wrapped grid with the arrow keys, by where they sit on screen. */
export interface Spot {
  left: number;
  top: number;
}

export type ArrowKey = "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown";

export function isArrow(key: string): key is ArrowKey {
  return key === "ArrowLeft" || key === "ArrowRight" || key === "ArrowUp" || key === "ArrowDown";
}

/** Cards within this many pixels vertically are on the same row. */
const ROW = 8;

/**
 * The index of the card the arrow leads to, or `current` at an edge. Left and right walk
 * reading order; up and down go to the nearest card in the row above or below.
 */
export function nextByArrow(spots: readonly Spot[], current: number, key: ArrowKey): number {
  const here = spots[current];
  if (!here) {
    return current;
  }
  if (key === "ArrowLeft") {
    return Math.max(0, current - 1);
  }
  if (key === "ArrowRight") {
    return Math.min(spots.length - 1, current + 1);
  }
  const down = key === "ArrowDown";
  let rowTop: number | null = null;
  for (const spot of spots) {
    const beyond = down ? spot.top > here.top + ROW : spot.top < here.top - ROW;
    if (!beyond) {
      continue;
    }
    if (rowTop === null || (down ? spot.top < rowTop : spot.top > rowTop)) {
      rowTop = spot.top;
    }
  }
  if (rowTop === null) {
    return current;
  }
  let best = current;
  let distance = Infinity;
  spots.forEach((spot, index) => {
    if (Math.abs(spot.top - rowTop) > ROW) {
      return;
    }
    const d = Math.abs(spot.left - here.left);
    if (d < distance) {
      distance = d;
      best = index;
    }
  });
  return best;
}
